import { buildRateCardPreview, type RateCardDraftContext } from "@/lib/rateCardPreview";
import { validateRateCard } from "@/lib/rateCardValidator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

export function RateCardSidebar({ ctx }: { ctx: RateCardDraftContext }) {
    const blocks = buildRateCardPreview(ctx);
    const lints = validateRateCard(ctx);
    const errors = lints.filter((l) => l.level === "error");
    const warnings = lints.filter((l) => l.level === "warning");

    return (
        <div className="space-y-4 lg:sticky lg:top-4">
            <Card className="border-border/60 shadow-sm">
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 font-medium">
                        <Eye className="h-4 w-4 text-primary" />
                        Live preview
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                    {blocks.map((block) => (
                        <div key={block.title}>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                                {block.title}
                            </p>
                            <ul className="space-y-0.5">
                                {block.lines.map((line, i) => (
                                    <li key={i} className="text-foreground/90 leading-snug">
                                        {line}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Card
                className={cn(
                    "border-border/60 shadow-sm",
                    errors.length > 0 && "border-destructive/40",
                    errors.length === 0 && warnings.length === 0 && lints.length === 0 && "border-emerald-500/30",
                )}
            >
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 font-medium">
                        {errors.length === 0 && warnings.length === 0 ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        ) : (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                        )}
                        Validator
                        {lints.length > 0 && (
                            <Badge variant="secondary" className="ml-auto text-[10px]">
                                {lints.length}
                            </Badge>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {lints.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No issues detected.</p>
                    ) : (
                        <ul className="space-y-2">
                            {lints.map((lint, i) => (
                                <li
                                    key={i}
                                    className={cn(
                                        "text-xs rounded-md px-2.5 py-2",
                                        lint.level === "error"
                                            ? "bg-destructive/10 text-destructive"
                                            : "bg-amber-500/10 text-amber-800 dark:text-amber-200",
                                    )}
                                >
                                    {lint.section && (
                                        <span className="font-semibold">{lint.section}: </span>
                                    )}
                                    {lint.message}
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
